import React, { useMemo, useRef, useState } from "react";
import { useAddressLookupQuery, Location } from "generated/graphql";
import { useTranslation } from "utilities/i18n";
import { DropDownLabel, PolyIcon } from "elements";
import { debounce } from "lodash";
import { ErrorMessage, useField } from "formik";

interface AddressInputProps {
  onSelect: (address: Partial<Location>) => void;
  tabIndex?: number;
  name: string;
  placeholder?: boolean;
  placeholderLabel?: string;
  inline?: boolean;
  disabled?: boolean;
  label?: string;
}

const addressDebounce = process.env.NODE_ENV !== "test" ? 300 : 0;

const AddressInput: React.FC<AddressInputProps> = props => {
  const { onSelect, tabIndex = 0 } = props;
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const self = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  let results: Array<Partial<Location>> = [];
  const { data, fetching } = useAddressLookupQuery({
    variables: {
      address: search || " " // defaulting to a space returns [], instead of an error
    }
  });

  const [field, contentMeta, contentHelpers] = useField({
    name: props.name
  });

  if (data && !fetching && data.postalLookup && search !== "") {
    results = data.postalLookup;
  }

  const updateSearchDebounce = useMemo(
    () => debounce((search: string) => setSearch(search), addressDebounce),
    [setSearch]
  );

  let wrapClasses: Array<string> = [];
  if (contentMeta.touched && contentMeta.error) {
    wrapClasses.push("has-error");
  }
  if (open || field.value) {
    wrapClasses.push("active");
  }
  return (
    <div
      className="address-select dropdown-list dropdown-group mt-0 pb-0"
      ref={self}
    >
      {props.label && (
        <DropDownLabel
          label={props.label}
          value={props.label}
          name={props.label}
        />
      )}
      <div
        data-testid="address-field"
        className={`textarea-wrapper input-wrapper ${open ? "open" : ""} ${
          props.disabled ? "disabled" : ""
        }`}
      >
        <div className={wrapClasses.join(" ")}>
          {props.placeholder && (
            <label id="address-input" className="hidden">
              {props.placeholderLabel ?? t("ADDRESS")}
            </label>
          )}
          <div
            className="textarea"
            contentEditable={!props.disabled}
            suppressContentEditableWarning
            aria-labelledby="address-input"
            data-testid="address-textarea"
            onInput={e => {
              if (!props.disabled) {
                updateSearchDebounce(e.currentTarget.innerText);
                setOpen(true);
              }
            }}
            onFocus={() => {
              if (!props.disabled) {
                setSearch(search || field.value);
                contentHelpers.setTouched(true);
                if (field.value) {
                  setOpen(true);
                }
              }
            }}
            onBlur={() => {
              setOpen(false);
            }}
            tabIndex={tabIndex}
            data-text={
              props.placeholder
                ? props.placeholderLabel
                  ? props.placeholderLabel
                  : t("ADDRESS")
                : ""
            }
          >
            {field.value}
          </div>
          {!props.disabled && (
            <div className={`${wrapClasses.join(" ")} icon-wrapper`}>
              <PolyIcon icon="search" />
            </div>
          )}
        </div>
      </div>

      <div className={`shadow-blocker ${open ? "visible" : ""}`} />
      <ul
        className={`options-list ${open ? "open" : ""} ${
          props.inline ? "inline" : ""
        }`}
      >
        {!fetching &&
          results.map((address, index) => (
            <li
              className="option"
              role="option"
              data-testid="address-option"
              aria-selected={false}
              // need onMouseDown instead of onClick to prevent onBlur executing first
              onMouseDown={() => {
                onSelect(address);
                setOpen(false);
                setSearch(address.formattedAddress || "");
              }}
              key={index + (address.formattedAddress || "")}
            >
              <span className="option-content">
                {address.formattedAddress ?? null}
              </span>
            </li>
          ))}
        {fetching && (
          <li className="option" key="loading">
            {t("LOADING")}
          </li>
        )}
        {!results.length && !fetching ? (
          <li key="no-results" className="option">
            {t("NO_ADDRESS_MATCH")}
          </li>
        ) : null}
      </ul>
      <div className={wrapClasses.join(" ")}>
        <ErrorMessage
          component="div"
          className="error-message"
          data-testid={`error-msg-for-${props.name}`}
          name={props.name}
        />
      </div>
    </div>
  );
};

export { AddressInput };
